import './BetbookPosts.less';

import clsx from 'clsx';
import React, { useEffect, useLayoutEffect } from 'react';
import { dispatch, useReframe } from 'nike-re-framejs';

import { navigate } from '@/routing';
import i18n from 'nike-ui/src/common/i18n';
import { useActiveRoute } from 'nike-ui/src/common/routing/NikeRouteContext';
import { COMMUNITY_ANALYSES, BETBOOK_TICKETS, NIKE1_MATCH, ONB_POST_REGISTRATION } from '@/routing/routes';
import AddInCircleIcon from '@/icons/AddInCircleIcon';
import BetbookPostItemLoader from '@/betbook/views/BetbookPostItemLoader';
import Button from '@/common/component/Button';
import EmptyState from 'nike-ui/src/components/empty-state/EmptyState';
import InfiniteScroll from '@/responsive/components/InfiniteScroll/InfiniteScroll';
import LoaderMini from '@/ui/views/LoaderMini';
import MultipleComponentsLoader from '@/common/components/loaders/MultipleComponentsLoader';
import { loadBetbookPosts, loadMoreBetbookPosts, setScroll } from '@/betbook/handlers';
import {
    hasNextPage$,
    initialBetbookPostsLoadingDone$,
    loadingBetbookPostsPending$,
    offset$,
    posts$,
    scrollPosition$
} from '@/betbook/subs';
import { accountVerificationStatus$, loggedIn$ } from '@/account/subs';
import {
    betbookFilterBetCount$,
    betbookFilterExpirationTag$,
    betbookFilterOdds$,
    betbookFilterSportIds$,
    betbookFilterStake$,
    betbookFilterWin$
} from 'nike-ui/src/components/betbook/subs';
import { POST_ANALYSIS, SORTING_NEW } from 'nike-ui/src/components/betbook/constants';
import { betbookNewRoute } from '@/betbook/utils';
import { useBetbookLastParam } from 'nike-ui/src/components/betbook/utils';
import BetbookAnalysisItem from '@/betbook/views/BetbookAnalysisItem';

function MatchBetbookPosts({ matchId, isResult }: { matchId: string; isResult: boolean }) {
    const postType = POST_ANALYSIS;
    const sortType = SORTING_NEW;
    const { subscribe } = useReframe();
    const offset = subscribe(offset$) || 0;
    const route = useActiveRoute();
    const isPrematchDetail = route === NIKE1_MATCH;
    const [, setLastParam] = useBetbookLastParam();
    const betCount = subscribe(betbookFilterBetCount$);
    const expirationTag = subscribe(betbookFilterExpirationTag$);
    const odds = subscribe(betbookFilterOdds$);
    const sportIds = subscribe(betbookFilterSportIds$);
    const stake = subscribe(betbookFilterStake$);
    const win = subscribe(betbookFilterWin$);
    const scrollPosition = subscribe(scrollPosition$) || 0;
    const posts = subscribe(posts$);
    const initialLoadingDone = subscribe(initialBetbookPostsLoadingDone$);
    const loadingPending = subscribe(loadingBetbookPostsPending$);
    const hasNextPage = subscribe(hasNextPage$);
    const loggedIn = subscribe(loggedIn$);
    const verificationStatus = subscribe(accountVerificationStatus$);
    const isVerified = verificationStatus === 'VERIFIED';
    const betbookViewPort = document.querySelector('.layout-main-col');

    useLayoutEffect(() => {
        if (betbookViewPort && window.matchMedia('(min-width: 800px)')) {
            betbookViewPort.scrollTo(0, scrollPosition);
        }
    }, [scrollPosition, betbookViewPort]);
    useLayoutEffect(() => {
        if (offset === 0 && (route === COMMUNITY_ANALYSES || route === BETBOOK_TICKETS)) {
            if (window.matchMedia('(max-width: 799px)').matches) {
                window.onbeforeunload = () => {
                    window.scrollTo(0, 0);
                };
                document.body.className = 'betbook-overflow';
            }
        }
    }, [offset, route]);
    useEffect(() => {
        dispatch(loadBetbookPosts(postType, sortType, sportIds, odds, stake, win, betCount, expirationTag, matchId));
        setLastParam({ postType, sortType, matchId });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [postType, sortType]);

    function handlePositionMemoize() {
        betbookViewPort && dispatch(setScroll(betbookViewPort?.scrollTop));
    }
    function createNewPost() {
        if (loggedIn) {
            navigate(betbookNewRoute(postType));
        } else {
            dispatch(['login.openScreen']);
        }
    }
    function handleLoadMore() {
        dispatch(loadMoreBetbookPosts(postType));
    }
    return (
        <div onClick={handlePositionMemoize}>
            {loggedIn && !isVerified && <NotVerifiedInfo verificationStatus={verificationStatus} />}
            {!isResult && !isPrematchDetail && (
                <button
                    className={clsx('betbook-add-post', {
                        'betbook-add-post-disabled': verificationStatus === 'UNVERIFIED'
                    })}
                    onClick={createNewPost}
                    disabled={verificationStatus === 'UNVERIFIED'}
                    data-atid="betbook-add-analysis"
                >
                    <AddInCircleIcon />
                    {i18n('betbook.posts.addNewPost')}
                </button>
            )}
            {initialLoadingDone ? (
                posts?.length ? (
                    <InfiniteScroll
                        className="betbook-posts"
                        hasMore={hasNextPage && !loadingPending}
                        loadMore={handleLoadMore}
                        loader={<LoaderMini className="infinite-loader betbook-more-posts-loader" key="loader" />}
                        threshold={0}
                        initialLoad={false}
                    >
                        {posts.map(post => (
                            <BetbookAnalysisItem key={post.id} post={post} />
                        ))}
                    </InfiniteScroll>
                ) : (
                    <EmptyState text={i18n('betbook.posts.empty')} />
                )
            ) : (
                <BetbookPostsLoader isResult={isResult} />
            )}
        </div>
    );
}

function NotVerifiedInfo({ verificationStatus }) {
    function finishRegistration(e) {
        e.stopPropagation();
        navigate(ONB_POST_REGISTRATION);
    }

    return (
        <div className="betbook-verification">
            {verificationStatus === 'UNVERIFIED' ? i18n('betbook.notVerified.text') : i18n('betbook.verifying.text')}
            {verificationStatus === 'UNVERIFIED' ? (
                <Button className="finish-registration-link" onClick={finishRegistration}>
                    {i18n('registration.leave.action.stay')}
                </Button>
            ) : null}
        </div>
    );
}

function BetbookPostsLoader({ isResult = false }) {
    return (
        <>
            {!isResult && (
                <button className="betbook-add-post" disabled>
                    <AddInCircleIcon />
                    {i18n('betbook.posts.addNewPost')}
                </button>
            )}
            <div className="betbook-posts">
                <MultipleComponentsLoader itemsCount={2} componentLoader={BetbookPostItemLoader} />
            </div>
        </>
    );
}

export default MatchBetbookPosts;
